// TopicNode — content-topic hierarchy node (per CONTENT_SPEC.md §1).
//
// Decision (Session 19K, signed CONTENT_SPEC v0.1.0):
//   - 3-level hierarchy: chapter → topic → subtopic
//   - Stored as flat array on IngestPackage; tree reconstructed via parentId
//   - Decision #1 (CONTENT_SPEC §11): id uses chapter-prefix for topic + subtopic
//     (e.g. "asymptotic_analysis__master_theorem"); avoids cross-chapter
//     id collisions while remaining computable from a sluggified name.
//
// Why flat (not nested children[]):
//   - AI emits nodes more easily as a list than as a tree
//   - Single parentId field is simpler than embedded recursion
//   - JSON Schema for AI prompt is cleaner
import { z } from 'zod';
export const TopicLevelSchema = z.enum(['chapter', 'topic', 'subtopic']);
export const TopicNodeSchema = z.object({
    /** Slug id. Chapter: bare slug. Topic: ${chapterId}__${topicSlug}. Subtopic: ${topicId}__${subtopicSlug}. */
    id: z.string().regex(/^[a-z][a-z0-9_]*(__[a-z][a-z0-9_]*)*$/, 'topic id must be snake_case with __ prefix'),
    level: TopicLevelSchema,
    /** Human-readable name (preserves capitalization + punctuation). */
    name: z.string().min(1).max(120),
    /** null only when level === 'chapter'. */
    parentId: z.string().nullable(),
}).passthrough();
/** Validate that a hierarchy array is structurally consistent. Returns issues if any. */
export function validateTopicHierarchy(nodes) {
    const issues = [];
    const ids = new Set();
    for (const n of nodes) {
        if (ids.has(n.id))
            issues.push(`duplicate node id: ${n.id}`);
        ids.add(n.id);
    }
    for (const n of nodes) {
        if (n.level === 'chapter') {
            if (n.parentId !== null)
                issues.push(`chapter ${n.id} must have parentId=null`);
        }
        else {
            if (n.parentId === null)
                issues.push(`${n.level} ${n.id} must have a parentId`);
            else if (!ids.has(n.parentId))
                issues.push(`${n.id} references missing parent ${n.parentId}`);
        }
    }
    return issues;
}
//# sourceMappingURL=topic-node.js.map